import { useEffect } from 'react';
import type { Variants } from 'framer-motion';
import { useShouldReduceAnimations } from './useDeviceDetection';

/**
 * Custom hook to initialize and manage AOS (Animate On Scroll) animations
 * Optimized for mobile performance (lazy-loaded + disabled when needed)
 */
export const useAOS = (config?: Record<string, any>) => {
  const shouldReduceAnimations = useShouldReduceAnimations();

  useEffect(() => {
    let mounted = true;

    const initAOS = async () => {
      if (!mounted || shouldReduceAnimations || window.innerWidth < 768) return;

      try {
        const AOS = (await import('aos')).default;
        await import('aos/dist/aos.css');

        AOS.init({
          duration: 600,
          once: true,
          offset: 80,
          easing: 'ease-out-cubic',
          ...config,
        });

        // Single refresh after init
        setTimeout(() => AOS.refresh(), 300);
      } catch (err) {
        console.warn('AOS failed to initialize:', err);
      }
    };

    // Delay initialization slightly to avoid blocking first render
    const timeout = setTimeout(initAOS, 400);

    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [config, shouldReduceAnimations]);
};

/**
 * Get motion variants optimized for mobile performance
 * Returns simplified variants when animations should be reduced
 */
export const useMotionVariants = () => {
  const shouldReduceAnimations = useShouldReduceAnimations();

  if (shouldReduceAnimations) {
    // Simplified variants (fade only)
    return {
      fadeIn: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      fadeInUp: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      fadeInDown: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      fadeInLeft: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      fadeInRight: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      scaleIn: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      scaleUp: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      staggerContainer: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } } as Variants,
      staggerContainerFast: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.03 } } } as Variants,
      slideInUp: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
      slideInDown: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } } as Variants,
    };
  }

  // Full desktop animations
  return motionVariants;
};

/**
 * Framer Motion variants for common animations
 * Enhanced for 2025 modern UI patterns
 */
export const motionVariants: Record<string, Variants> = {
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6 } },
  },

  fadeInUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  },

  fadeInDown: {
    hidden: { opacity: 0, y: -40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  },

  fadeInLeft: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
  },

  fadeInRight: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
  },

  scaleIn: {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
  },

  scaleUp: {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
  },

  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.05 },
    },
  },

  staggerContainerFast: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.02 },
    },
  },

  slideInUp: {
    hidden: { y: 100, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.7 } },
  },

  slideInDown: {
    hidden: { y: -100, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.7 } },
  },
};

/**
 * Viewport settings for Framer Motion whileInView animations
 * Optimized for mobile performance
 */
export const useViewportSettings = () => {
  const shouldReduceAnimations = useShouldReduceAnimations();

  return {
    once: true,
    amount: shouldReduceAnimations ? 0.1 : 0.2,
    margin: shouldReduceAnimations ? '0px' : '-50px',
  };
};

/**
 * Static viewport settings for legacy components
 * @deprecated Use useViewportSettings() for dynamic optimization
 */
export const viewportSettings = {
  once: true,
  amount: 0.2,
  margin: '-50px',
};

/**
 * Intersection Observer options for manual control
 */
export const intersectionObserverOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -100px 0px',
};
